import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTION_NAMES } from '@/lib/collections';
import { Logger } from '@/lib/logger';
import { getMonthsAgoString, THREE_MONTHS, TWELVE_MONTHS } from '@/shared/utils/dateUtils';
import type { IntelCoreWorkflowRecord, WorkflowConfigRecord } from '@/shared/types/intel/intelData';
import type {
  WorkflowHealthData,
  OrgHealthData,
} from '@/shared/types/intel/workflowHealth';
import type { WorkflowPerformanceData } from '@/shared/types/intel/workflowPerformance';

export type { WorkflowHealthData, OrgHealthData };

const logger = Logger.createScoped('WorkflowDataService');

const COLLECTION_NAME = COLLECTION_NAMES.INTEL_CORE_WORKFLOW;

/**
 * Organization data structure
 */
export interface OrganizationData {
  org_id: string;
  org_name: string;
}

/**
 * Workflow data structure
 */
export interface WorkflowData {
  workflow_id: string;
  workflow_name: string;
  org_id: string;
}

/**
 * Complete workflow data with organization info
 */
export interface WorkflowWithOrg extends WorkflowData {
  org_name: string;
  workflow_type?: string;
}

/**
 * Fetch unique organizations from intel_core_workflow collection
 * Filters for records from the last 3 months
 *
 * @returns Array of unique organizations sorted by name
 */
export async function fetchOrganizations(): Promise<OrganizationData[]> {
  try {
    const threeMonthsAgoString = getMonthsAgoString(THREE_MONTHS);

    // Query collection with date filter
    // Note: month is stored as nested object {value: "YYYY-MM-DD"}
    const q = query(
      collection(db, COLLECTION_NAME),
      where('month.value', '>=', threeMonthsAgoString)
    );

    const querySnapshot = await getDocs(q);

    // Use a Map to collect unique organizations by org_id
    const orgMap = new Map<string, OrganizationData>();

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const org_id = data.org_id;
      const org_name = data.org_name;

      if (org_id && org_name && !orgMap.has(org_id)) {
        orgMap.set(org_id, { org_id, org_name });
      }
    });

    // Convert to array and sort by name
    const organizations = Array.from(orgMap.values()).sort((a, b) =>
      a.org_name.localeCompare(b.org_name)
    );

    return organizations;
  } catch (error) {
    logger.error('Error fetching organizations:', error);
    throw error;
  }
}

/**
 * Fetch all workflows with their organization data from intel_core_workflow collection
 * Filters for records from the last 3 months
 *
 * @returns Array of workflows with organization info
 */
export async function fetchWorkflows(): Promise<WorkflowWithOrg[]> {
  try {
    const threeMonthsAgoString = getMonthsAgoString(THREE_MONTHS);

    // Query collection with date filter
    // Note: month is stored as nested object {value: "YYYY-MM-DD"}
    const q = query(
      collection(db, COLLECTION_NAME),
      where('month.value', '>=', threeMonthsAgoString)
    );

    const querySnapshot = await getDocs(q);

    // Use a Map to collect unique workflows by workflow_id
    const workflowMap = new Map<string, WorkflowWithOrg>();

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const workflow_id = data.workflow_id;
      const workflow_name = data.workflow_name;
      const org_id = data.org_id;
      const org_name = data.org_name;

      if (workflow_id && workflow_name && org_id && org_name && !workflowMap.has(workflow_id)) {
        workflowMap.set(workflow_id, {
          workflow_id,
          workflow_name,
          org_id,
          org_name,
          workflow_type: data.workflow_type || undefined,
        });
      }
    });

    // Convert to array and sort by name
    const workflows = Array.from(workflowMap.values()).sort((a, b) =>
      a.workflow_name.localeCompare(b.workflow_name)
    );

    return workflows;
  } catch (error) {
    logger.error('Error fetching workflows:', error);
    throw error;
  }
}

/**
 * Fetch workflows filtered by organization ID
 *
 * @param orgId - The organization ID to filter by
 * @returns Array of workflows for the specified organization
 */
export function getWorkflowsByOrg(
  workflows: WorkflowWithOrg[],
  orgId: string
): WorkflowData[] {
  return workflows
    .filter((w) => w.org_id === orgId)
    .map(({ workflow_id, workflow_name, org_id }) => ({
      workflow_id,
      workflow_name,
      org_id,
    }))
    .sort((a, b) => a.workflow_name.localeCompare(b.workflow_name));
}

/**
 * Parse workflow health data from Firestore document
 */
function parseWorkflowHealthData(doc: any): WorkflowHealthData | null {
  try {
    const data = doc.data();

    // Ensure required fields exist
    if (!data.workflow_id || !data.month?.value) {
      return null;
    }

    const revenue = parseFloat(data.workflow_revenue) || 0;
    const fte = parseFloat(data.workflow_fte) || 0;

    return {
      workflow_id: data.workflow_id,
      workflow_name: data.workflow_name || '',
      org_id: data.org_id || '',
      org_name: data.org_name || '',
      month: data.month.value,
      workflow_spend: parseFloat(data.workflow_spend) || 0,
      workflow_revenue: revenue,
      workflow_fte: fte,
      rev_per_fte: fte > 0 ? revenue / fte : 0,
      health_score: parseFloat(data.healthscore) || 0,
      workflow_lead: data.workflow_lead || '',
      workflow_headcount: parseFloat(data.workflow_headcount) || 0,
      workflow_complexity: data.workflow_complexity || 'Medium',
      healthscore_poc_change: parseFloat(data.healthscore_poc_change) || 0,
      healthscore_performance_vs_target: parseFloat(data.healthscore_performance_vs_target) || 0,
      healthscore_new_ideas: parseFloat(data.healthscore_new_ideas) || 0,
      healthscore_good_working_relationship: parseFloat(data.healthscore_good_working_relationship) || 0,
      healthscore_creative_assets: parseFloat(data.healthscore_creative_assets) || 0,
      healthscore_reporting: parseFloat(data.healthscore_reporting) || 0,
    };
  } catch (error) {
    logger.error('Error parsing workflow health data:', error);
    return null;
  }
}

/**
 * Fetch health data for a specific workflow
 * Returns all months for the workflow from the last 3 months
 *
 * @param workflowId - The workflow ID to fetch data for
 * @returns Array of health data points for the workflow
 */
export async function fetchWorkflowHealthData(
  workflowId: string,
  monthsBack: number = TWELVE_MONTHS
): Promise<WorkflowHealthData[]> {
  try {
    const startDateString = getMonthsAgoString(monthsBack);

    const q = query(
      collection(db, COLLECTION_NAME),
      where('workflow_id', '==', workflowId),
      where('month.value', '>=', startDateString)
    );

    const querySnapshot = await getDocs(q);

    const healthData: WorkflowHealthData[] = [];

    querySnapshot.forEach((doc) => {
      const parsed = parseWorkflowHealthData(doc);
      if (parsed) {
        healthData.push(parsed);
      }
    });

    // Sort by month descending (newest first)
    healthData.sort((a, b) => b.month.localeCompare(a.month));

    return healthData;
  } catch (error) {
    logger.error('Error fetching workflow health data:', error);
    throw error;
  }
}

/**
 * Fetch and aggregate health data for an organization
 * Returns all months for the org from the specified number of months back
 *
 * @param orgId - The organization ID to fetch data for
 * @param monthsBack - Number of months to look back (default: 14 for year-over-year with data lag)
 * @returns Array of aggregated org health data by month
 */
export async function fetchOrgHealthData(
  orgId: string,
  monthsBack: number = 14
): Promise<OrgHealthData[]> {
  try {
    const monthsAgoString = getMonthsAgoString(monthsBack);

    const q = query(
      collection(db, COLLECTION_NAME),
      where('org_id', '==', orgId),
      where('month.value', '>=', monthsAgoString)
    );

    const querySnapshot = await getDocs(q);

    // Group workflows by month
    const monthMap = new Map<string, WorkflowHealthData[]>();
    let orgName = '';

    querySnapshot.forEach((doc) => {
      const parsed = parseWorkflowHealthData(doc);
      if (parsed) {
        if (!orgName) orgName = parsed.org_name;

        if (!monthMap.has(parsed.month)) {
          monthMap.set(parsed.month, []);
        }
        monthMap.get(parsed.month)!.push(parsed);
      }
    });

    // Aggregate by month
    const orgHealthData: OrgHealthData[] = [];

    monthMap.forEach((workflows, month) => {
      const totalRevenue = workflows.reduce((sum, w) => sum + w.workflow_revenue, 0);
      const totalSpend = workflows.reduce((sum, w) => sum + w.workflow_spend, 0);
      const totalFte = workflows.reduce((sum, w) => sum + w.workflow_fte, 0);

      // Calculate weighted average health score based on revenue share
      let weightedHealthScore = 0;
      if (totalRevenue > 0) {
        weightedHealthScore = workflows.reduce((sum, w) => {
          const weight = w.workflow_revenue / totalRevenue;
          return sum + (w.health_score * weight);
        }, 0);
      }

      orgHealthData.push({
        org_id: orgId,
        org_name: orgName,
        month,
        total_spend: totalSpend,
        total_revenue: totalRevenue,
        total_fte: totalFte,
        rev_per_fte: totalFte > 0 ? totalRevenue / totalFte : 0,
        avg_health_score: weightedHealthScore,
        workflows,
      });
    });

    // Sort by month descending (newest first)
    orgHealthData.sort((a, b) => b.month.localeCompare(a.month));

    return orgHealthData;
  } catch (error) {
    logger.error('Error fetching org health data:', error);
    throw error;
  }
}

/**
 * Parse workflow performance data from Firestore document
 */
function parseWorkflowPerformanceData(doc: any): WorkflowPerformanceData | null {
  try {
    const data = doc.data();

    // Ensure required fields exist
    if (!data.workflow_id || !data.month?.value) {
      return null;
    }

    return {
      workflow_id: data.workflow_id,
      workflow_name: data.workflow_name || '',
      org_id: data.org_id || '',
      org_name: data.org_name || '',
      month: data.month.value,
      workflow_spend: parseFloat(data.workflow_spend) || 0,
      workflow_revenue: parseFloat(data.workflow_revenue) || 0,
      workflow_objective: data.workflow_objective || 'Revenue',
      workflow_result: parseFloat(data.workflow_result) || 0,
      healthscore_performance_vs_target: parseFloat(data.healthscore_performance_vs_target) || 0,
    };
  } catch (error) {
    logger.error('Error parsing workflow performance data:', error);
    return null;
  }
}

/**
 * Fetch performance data for a specific workflow
 * Returns all months for the workflow from the last 12 months
 *
 * @param workflowId - The workflow ID to fetch data for
 * @returns Array of performance data points for the workflow
 */
export async function fetchWorkflowPerformanceData(
  workflowId: string
): Promise<WorkflowPerformanceData[]> {
  try {
    const twelveMonthsAgoString = getMonthsAgoString(TWELVE_MONTHS);

    const q = query(
      collection(db, COLLECTION_NAME),
      where('workflow_id', '==', workflowId),
      where('month.value', '>=', twelveMonthsAgoString)
    );

    const querySnapshot = await getDocs(q);

    const performanceData: WorkflowPerformanceData[] = [];

    querySnapshot.forEach((doc) => {
      const parsed = parseWorkflowPerformanceData(doc);
      if (parsed) {
        performanceData.push(parsed);
      }
    });

    // Sort by month descending (newest first)
    performanceData.sort((a, b) => b.month.localeCompare(a.month));

    return performanceData;
  } catch (error) {
    logger.error('Error fetching workflow performance data:', error);
    throw error;
  }
}

/**
 * Workflow basic info structure
 */
export interface WorkflowBasicInfo {
  workflow_id: string;
  workflow_name: string;
  workflow_balance: string;
  org_id: string;
  org_name: string;
}

/**
 * Fetch basic workflow info for multiple workflow IDs (current month only)
 * Used to enrich user's other projects with workflow details
 *
 * @param workflowIds - Array of workflow IDs to fetch
 * @returns Map of workflow_id to workflow basic info
 */
export async function fetchWorkflowBasicInfo(
  workflowIds: string[]
): Promise<Map<string, WorkflowBasicInfo>> {
  try {
    if (workflowIds.length === 0) return new Map();

    const threeMonthsAgoString = getMonthsAgoString(THREE_MONTHS);

    const q = query(
      collection(db, COLLECTION_NAME),
      where('month.value', '>=', threeMonthsAgoString)
    );

    const querySnapshot = await getDocs(q);

    const workflowMap = new Map<string, WorkflowBasicInfo>();

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const workflow_id = data.workflow_id;

      // Only include workflows we're looking for and get most recent data
      if (workflowIds.includes(workflow_id) && !workflowMap.has(workflow_id)) {
        workflowMap.set(workflow_id, {
          workflow_id,
          workflow_name: data.workflow_name || '',
          workflow_balance: data.workflow_balance || '',
          org_id: data.org_id || '',
          org_name: data.org_name || '',
        });
      }
    });

    return workflowMap;
  } catch (error) {
    logger.error('Error fetching workflow basic info:', error);
    throw error;
  }
}

/**
 * Fetches all intel_core_workflow records for a configurable time period.
 * This is the primary data source for Overview, Health, and Projects views.
 *
 * @param monthsBack - Number of months to look back (12 or 13)
 * @param excludeStrategicAnalytics - Whether to filter out "Strategic & Analytics" workflows
 * @returns Promise<IntelCoreWorkflowRecord[]>
 */
export async function fetchAllWorkflowCoreData(
  monthsBack: number = 12,
  excludeStrategicAnalytics: boolean = false
): Promise<IntelCoreWorkflowRecord[]> {
  const startDate = getMonthsAgoString(monthsBack);

  const q = query(
    collection(db, COLLECTION_NAME),
    where('month.value', '>=', startDate)
  );

  const snapshot = await getDocs(q);

  let records = snapshot.docs.map(doc => {
    const data = doc.data();

    // Parse numeric fields that may come as strings from BigQuery/Firestore
    // healthscore and healthscore_performance_vs_target are often stored as strings
    const rawHealthscore = data.healthscore;
    const healthscore = typeof rawHealthscore === 'string'
      ? parseFloat(rawHealthscore)
      : (rawHealthscore ?? null);

    const rawPerfVsTarget = data.healthscore_performance_vs_target;
    const healthscore_performance_vs_target = typeof rawPerfVsTarget === 'string'
      ? parseFloat(rawPerfVsTarget)
      : (rawPerfVsTarget ?? null);

    return {
      workflow_id: data.workflow_id ?? '',
      workflow_name: data.workflow_name ?? '',
      org_id: data.org_id ?? '',
      org_name: data.org_name ?? '',
      workflow_type: data.workflow_type || undefined,
      month: data.month ?? { value: '' },
      healthscore,
      healthscore_category: data.healthscore_category ?? null,
      healthscore_performance_vs_target,
      fte_total: data.fte_total ?? 0,
      fte_senior: data.fte_senior ?? 0,
      fte_junior: data.fte_junior ?? 0,
      fte_thriving: data.fte_thriving ?? 0,
      fte_surviving: data.fte_surviving ?? 0,
      workflow_fte: parseFloat(data.workflow_fte) || 0,
      workflow_revenue: parseFloat(data.workflow_revenue) || 0,
      workflow_manager_fte: parseFloat(data.workflow_manager_fte) || 0,
      workflow_strategist_fte: parseFloat(data.workflow_strategist_fte) || 0,
      workflow_lead_fte: parseFloat(data.workflow_lead_fte) || 0,
      workflow_specialist_fte: parseFloat(data.workflow_specialist_fte) || 0,
      headcount_total: data.headcount_total ?? 0,
      revenue: data.revenue ?? 0,
      rev_per_fte: data.rev_per_fte ?? 0,
      balance_digital_trad: data.balance_digital_trad ?? null,
      balance_account_creative: data.balance_account_creative ?? null,
      // Additional metadata (for useProjectsData)
      workflow_headcount: parseFloat(data.workflow_headcount) || 0,
      workflow_balance: data.workflow_balance ?? '',
    };
  }) as IntelCoreWorkflowRecord[];

  if (excludeStrategicAnalytics) {
    records = records.filter(r => r.workflow_name !== 'Strategic & Analytics');
  }

  return records;
}

/**
 * Fetches all workflow configurations and returns as a Map for O(1) lookups.
 * Used for enriching workflow data with division/program information.
 *
 * @returns Promise<Map<string, WorkflowConfigRecord>> - Map keyed by workflow_id
 */
export async function fetchWorkflowConfigs(): Promise<Map<string, WorkflowConfigRecord>> {
  const snapshot = await getDocs(collection(db, COLLECTION_NAMES.WORKFLOW_CONFIGS));

  const configMap = new Map<string, WorkflowConfigRecord>();

  snapshot.docs.forEach(doc => {
    const data = doc.data();
    // Note: Firestore uses snake_case (allocation_config), we normalize to camelCase
    const allocationConfig = data.allocation_config;

    configMap.set(doc.id, {
      workflow_id: doc.id,
      workflow_name: data.workflowName ?? data.workflow_name ?? '',
      allocationConfig: allocationConfig ? {
        division: allocationConfig.division,
        program: allocationConfig.program,
        team: allocationConfig.team,
      } : undefined,
    });
  });

  return configMap;
}

export interface WorkflowFteByMonth {
  month: string;
  totalFte: number;
}

export async function fetchWorkflowFteByMonths(
  earliestMonth: string,
): Promise<WorkflowFteByMonth[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('month.value', '>=', earliestMonth),
  );

  const snapshot = await getDocs(q);

  const fteByMonth = new Map<string, number>();
  snapshot.forEach((doc) => {
    const data = doc.data();
    const month = data.month?.value as string | undefined;
    const fte = parseFloat(data.workflow_fte) || 0;
    if (month) {
      fteByMonth.set(month, (fteByMonth.get(month) || 0) + fte);
    }
  });

  return Array.from(fteByMonth.entries()).map(([month, totalFte]) => ({
    month,
    totalFte,
  }));
}
